import PropTypes from "prop-types";
import { NavLink as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
// @mui
import { Box, List, ListItemText } from "@mui/material";
//
import { StyledNavItem, StyledNavItemIcon } from "./styles";
import navConfig from "./navConfig";
import { checkAuthorized } from "../../utils/checkAuthorized";
import { selectEffectiveRoles } from "../../features/rolePreview/rolePreviewSlice";
import { useGetTenantPlanQuery } from "../../features/tenants/tenantsApiSlice";
import { normalizePlanId } from "../../utils/planFeatures";
import usePlanGate from "../../hooks/usePlanGate";
import useAuth from "../../hooks/useAuth";
import FeatureLockedDialog from "../dialog/FeatureLockedDialog";
import { ROLES_LIST } from "../../features/roles/roles.config";

// ----------------------------------------------------------------------

export default function NavSection({ ...other }) {
  const roles = useSelector(selectEffectiveRoles);
  const { isUser, isEditor, isAdmin, isSuperAdmin } = useAuth();
  const hasStaffRole = isUser || isEditor || isAdmin || isSuperAdmin;
  const { data: tenant } = useGetTenantPlanQuery();
  const tenantPlanId = tenant?.billingPlan
    ? normalizePlanId(tenant?.billingPlan)
    : "";
  const eDojangOverrideEnabled = Boolean(tenant?.featureOverrides?.eDojang);

  return (
    <Box {...other}>
      <List disablePadding sx={{ p: 1 }}>
        {navConfig.map(
          (item) =>
            checkAuthorized(item?.role, roles) &&
            !(item.memberOnly && hasStaffRole) && (
              <NavItem
                key={item.path}
                item={item}
                tenantPlanId={tenantPlanId}
                eDojangOverrideEnabled={eDojangOverrideEnabled}
              />
            )
        )}
      </List>
    </Box>
  );
}

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  tenantPlanId: PropTypes.string,
  eDojangOverrideEnabled: PropTypes.bool,
};

function NavItem({ item, tenantPlanId, eDojangOverrideEnabled }) {
  const { title, path, icon, featureKey } = item;
  const roles = useSelector(selectEffectiveRoles);
  const isMitglied =
    Array.isArray(roles) &&
    roles.includes(ROLES_LIST.Mitglied) &&
    !roles.some((r) =>
      [
        ROLES_LIST.User,
        ROLES_LIST.Editor,
        ROLES_LIST.Admin,
        ROLES_LIST.SuperAdmin,
      ].includes(r)
    );
  const {
    isLoading,
    isAllowed,
    isDialogOpen,
    requiredPlanLabel,
    openDialog,
    closeDialog,
    goToUpgrade,
  } = usePlanGate(featureKey, {
    planId: tenantPlanId || undefined,
    skipTenantQuery: Boolean(tenantPlanId),
    eDojangOverrideEnabled,
  });

  const handleClick = (event) => {
    if (isLoading) {
      event.preventDefault();
      return;
    }
    if (isAllowed) return;
    event.preventDefault();
    openDialog();
  };

  return (
    <>
      <StyledNavItem
        component={RouterLink}
        to={path}
        onClick={handleClick}
        data-tour={path.split("/").pop()}
        sx={{
          "&.active": {
            color: "white",
            bgcolor: "action.selected",
            fontWeight: "fontWeightBold",
          },
        }}
      >
        <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>
        <ListItemText disableTypography primary={title} />
      </StyledNavItem>
      <FeatureLockedDialog
        open={isDialogOpen}
        onClose={closeDialog}
        onUpgrade={isMitglied ? undefined : goToUpgrade}
        upgradePlanLabel={isMitglied ? "" : requiredPlanLabel}
        memberView={isMitglied}
      />
    </>
  );
}

NavItem.defaultProps = {
  tenantPlanId: "",
  eDojangOverrideEnabled: false,
};
